import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
  Form,
  Label,
  Input,
  Button,
  FormGroup,
  FormFeedback,
} from 'reactstrap';
import { useFormik } from 'formik';
import { createOrUpdateCountry } from '../../../actions/organization';
import { alertError, alertSuccess } from '../../../helpers/errorHandling';
import { useDispatch, useSelector } from 'react-redux';
import InnerLoader from '../../../Components/Common/InnerLoader';
import 'react-toastify/dist/ReactToastify.css';
import ReactSelect from 'react-select';

const countryData = {
  ae: {
    name: 'UAE',
    currency: 'AED',
    timezone: 'Asia/Dubai',
    dialingCode: '+971',
    flag: '🇦🇪',
  },
  om: {
    name: 'Oman',
    currency: 'OMR',
    timezone: 'Asia/Muscat',
    dialingCode: '+968',
    flag: '🇴🇲',
  },
  bh: {
    name: 'Bahrain',
    currency: 'BHD',
    timezone: 'Asia/Bahrain',
    dialingCode: '+973',
    flag: '🇧🇭',
  },
  qa: {
    name: 'Qatar',
    currency: 'QAR',
    timezone: 'Asia/Qatar',
    dialingCode: '+974',
    flag: '🇶🇦',
  },
  kw: {
    name: 'Kuwait',
    currency: 'KWD',
    timezone: 'Asia/Kuwait',
    dialingCode: '+965',
    flag: '🇰🇼',
  },
  sa: {
    name: 'Saudi Arabia',
    currency: 'SAR',
    timezone: 'Asia/Riyadh',
    dialingCode: '+966',
    flag: '🇸🇦',
  },
};

const CreateCountryForm = ({
  countryCode = null,
  isFromPopup = false,
  handleClose = () => {},
}) => {
  const dispatch = useDispatch();
  const { countries } = useSelector((state) => state?.organization);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('Please select a country'),
    countryCode: Yup.string()
      .matches(/^[a-z]{2}$/, 'Country code must be two lowercase letters')
      .required('Country code is required'),
    currency: Yup.string()
      .length(3, 'Currency code must be 3 characters long')
      .required('Currency is required'),
    timezone: Yup.string()
      .matches(/^([A-Za-z]+\/[A-Za-z_]+)$/, 'Invalid timezone format')
      .required('Timezone is required'),
    dialingCode: Yup.string()
      .matches(/^\+?\d+$/, 'Dialing code must be a valid number')
      .required('Dialing code is required'),
    primaryLanguage: Yup.string().required('Primary language is required'),
    secondaryLanguages: Yup.array().nullable(),
    flag: Yup.string().required('Flag is required'),
    isDisabled: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      countryCode: '',
      currency: '',
      timezone: '',
      dialingCode: '',
      primaryLanguage: 'en',
      secondaryLanguages: ['ar'],
      flag: '',
      isDisabled: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const res = await createOrUpdateCountry(
          { input: { ...values } },
          dispatch
        );
        if (res?._id) {
          if (values?._id) {
            alertSuccess('Country updated successfully');
          } else {
            alertSuccess('Country created successfully');
          }
          if (isFromPopup) {
            setTimeout(() => {
              resetFormValues();
              handleClose();
            }, 1000);
          }
        } else {
          alertError('Failed, Please try again later');
        }
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      } catch (e) {
        console.log(e);
      }
    },
  });

  const {
    resetForm,
    setValues,
    handleSubmit,
    setFieldValue,
    handleChange,
    values,
    touched,
    errors,
  } = formik;

  useEffect(() => {
    if (countryCode) {
      const country = countries?.find(
        (country) => country?.countryCode === countryCode
      );
      if (country?.countryCode) {
        setValues({
          _id: country?._id,
          name: country?.name,
          countryCode: country?.countryCode,
          currency: country?.currency,
          timezone: country?.timezone,
          dialingCode: country?.dialingCode,
          primaryLanguage: country?.primaryLanguage,
          secondaryLanguages: country?.secondaryLanguages,
          flag: country?.flag,
          isDisabled: country?.isDisabled,
          isDeleted: country?.isDeleted,
        });
      }
    }
  }, [countryCode, countries]);

  const resetFormValues = () => {
    resetForm({
      values: {
        name: '',
        countryCode: '',
        currency: '',
        timezone: '',
        dialingCode: '',
        primaryLanguage: 'en',
        secondaryLanguages: ['ar'],
        flag: '',
        isDisabled: false,
        isDeleted: false,
      },
    });
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <div className="d-flex flex-row flex-wrap w-100 gap-3">
          {/* Country Selection */}
          <FormGroup className="col-5 mb-0">
            <Label for="country">Country</Label>
            <Input
              type="select"
              name="name"
              id="name"
              onChange={(e) => {
                const selected = e.target.value;
                const countryInfo = countryData[selected];
                if (countryInfo) {
                  setFieldValue('name', countryInfo?.name);
                  setFieldValue('countryCode', selected);
                  setFieldValue('currency', countryInfo.currency);
                  setFieldValue('timezone', countryInfo.timezone);
                  setFieldValue('dialingCode', countryInfo.dialingCode);
                  setFieldValue('flag', countryInfo.flag);
                }
              }}
              value={values?.countryCode}
              invalid={touched.name && errors.name}
            >
              <option value="">Select a country</option>
              {Object.keys(countryData).map((countryCode) => (
                <option key={countryCode} value={countryCode}>
                  {countryData[countryCode].name}
                </option>
              ))}
            </Input>
            {touched.name && errors.name && (
              <FormFeedback>{errors.name}</FormFeedback>
            )}
          </FormGroup>

          {/* Country Info */}
          {values?.countryCode && (
            <>
              <FormGroup className="col-5 mb-0">
                <Label for="countryCode">Country Code</Label>
                <Input
                  type="text"
                  name="countryCode"
                  id="countryCode"
                  value={values.countryCode}
                  onChange={handleChange}
                  invalid={touched.countryCode && errors.countryCode}
                />
                {touched.countryCode && errors.countryCode && (
                  <FormFeedback>{errors.countryCode}</FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="col-5 mb-0">
                <Label for="currency">Currency</Label>
                <Input
                  type="text"
                  name="currency"
                  id="currency"
                  value={values.currency}
                  onChange={handleChange}
                  invalid={touched.currency && errors.currency}
                />
                {touched.currency && errors.currency && (
                  <FormFeedback>{errors.currency}</FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="col-5 mb-0">
                <Label for="timezone">Timezone</Label>
                <Input
                  type="text"
                  name="timezone"
                  id="timezone"
                  value={values.timezone}
                  onChange={handleChange}
                  invalid={touched.timezone && errors.timezone}
                />
                {touched.timezone && errors.timezone && (
                  <FormFeedback>{errors.timezone}</FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="col-5 mb-0">
                <Label for="dialingCode">Dialing Code</Label>
                <Input
                  type="text"
                  name="dialingCode"
                  id="dialingCode"
                  value={values.dialingCode}
                  onChange={handleChange}
                  invalid={touched.dialingCode && errors.dialingCode}
                />
                {touched.dialingCode && errors.dialingCode && (
                  <FormFeedback>{errors.dialingCode}</FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="col-5 mb-0">
                <Label for="flag">Flag</Label>
                <div className="form-control">
                  {values.flag ? values.flag : 'No flag selected'}
                </div>
              </FormGroup>

              <FormGroup className="col-5 mb-0">
                <Label for="primaryLanguage">Primary Language</Label>
                <Input
                  type="select"
                  name="primaryLanguage"
                  id="primaryLanguage"
                  value={values.primaryLanguage}
                  onChange={handleChange}
                  invalid={touched.primaryLanguage && errors.primaryLanguage}
                >
                  <option value="en">English</option>
                  <option value="ar">Arabic</option>
                  <option value="fr">French</option>
                  <option value="es">Spanish</option>
                </Input>
                {touched.primaryLanguage && errors.primaryLanguage && (
                  <FormFeedback>{errors.primaryLanguage}</FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-0 col-10">
                <Label for="secondaryLanguages">Secondary Languages</Label>
                <ReactSelect
                  isMulti
                  name="secondaryLanguages"
                  options={[
                    { value: 'ar', label: 'Arabic' },
                    { value: 'fr', label: 'French' },
                    { value: 'es', label: 'Spanish' },
                  ]}
                  value={values.secondaryLanguages.map((lang) => ({
                    value: lang,
                    label:
                      lang === 'ar'
                        ? 'Arabic'
                        : lang === 'fr'
                        ? 'French'
                        : 'Spanish',
                  }))}
                  onChange={(selectedOptions) =>
                    setFieldValue(
                      'secondaryLanguages',
                      selectedOptions.map((option) => option.value)
                    )
                  }
                  classNamePrefix="react-select"
                />
                {touched.secondaryLanguages && errors.secondaryLanguages && (
                  <FormFeedback className="d-block">
                    {errors.secondaryLanguages}
                  </FormFeedback>
                )}
              </FormGroup>
            </>
          )}

          {countryCode && (
            <FormGroup className="d-flex align-items-center col-10 mb-0">
              <Label for="isDisabled" className="me-2 mb-0">
                Is Disabled
              </Label>
              <div className="form-switch">
                <Input
                  type="switch"
                  name="isDisabled"
                  id="isDisabled"
                  checked={values.isDisabled}
                  onChange={handleChange}
                />
              </div>
            </FormGroup>
          )}

          <div className="col-10">
            <Button
              type="submit"
              color="primary"
              className="mt-2 bg-gradient px-5 "
              size="md"
            >
              {countryCode ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </Form>

      {loading && <InnerLoader />}
    </>
  );
};

export default CreateCountryForm;
